import { useState, useEffect } from 'react';
import { Check, X } from 'lucide-react';
import { membershipService } from '../services/membershipService';
import Table from '../components/ui/Table';
import Button from '../components/ui/Button';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { formatDate, formatCurrency } from '../../utils/formatters';

export default function MembershipPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejecting, setRejecting] = useState(false);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const data = await membershipService.getPending();
      setItems(data);
    } catch (error) {
      console.error('Error fetching pending payments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPending();
  }, []);

  const handleConfirm = async (item) => {
    setActioning(item.payment_id);
    try {
      await membershipService.confirm(item.user_id, item.payment_id);
      setItems((prev) => prev.filter((i) => i.payment_id !== item.payment_id));
    } catch (error) {
      console.error('Error confirming payment:', error);
    } finally {
      setActioning(null);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setRejecting(true);
    try {
      await membershipService.reject(rejectTarget.user_id, rejectTarget.payment_id);
      setItems((prev) => prev.filter((i) => i.payment_id !== rejectTarget.payment_id));
      setRejectTarget(null);
    } catch (error) {
      console.error('Error rejecting payment:', error);
    } finally {
      setRejecting(false);
    }
  };

  const columns = [
    {
      key: 'first_name',
      label: 'ФИО',
      render: (value, row) => (
        <div>
          <span className="font-medium">{`${value || ''} ${row.last_name || ''}`.trim()}</span>
          <div className="text-xs text-gray-400">@{row.username}</div>
        </div>
      ),
    },
    {
      key: 'phone',
      label: 'Телефон',
      render: (value) => value || '-',
    },
    {
      key: 'iin',
      label: 'ИИН',
      render: (value) => value || '-',
    },
    {
      key: 'amount',
      label: 'Сумма',
      render: (value) => formatCurrency(value),
    },
    {
      key: 'receipt_url',
      label: 'Чек',
      render: (value) =>
        value ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:text-primary-800 underline"
          >
            Открыть
          </a>
        ) : (
          '-'
        ),
    },
    {
      key: 'created_at',
      label: 'Дата запроса',
      render: (value) => formatDate(value),
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Взносы на проверке</h1>
        <p className="mt-1 text-sm text-gray-500">
          Сверьте телефон/ИИН с выпиской Kaspi и подтвердите или отклоните платёж. Всего: {loading ? '...' : items.length}
        </p>
      </div>

      <Table
        columns={columns}
        data={items}
        loading={loading}
        actions={(row) => (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="primary"
              size="sm"
              loading={actioning === row.payment_id}
              onClick={() => handleConfirm(row)}
            >
              <Check className="h-4 w-4 mr-1" />
              Подтвердить
            </Button>
            <button
              onClick={() => setRejectTarget(row)}
              className="text-red-500 hover:text-red-700"
              title="Отклонить"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
      />

      <ConfirmDialog
        isOpen={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={handleReject}
        title="Отклонение платежа"
        message="Вы уверены, что хотите отклонить этот платёж? Пользователь увидит, что взнос не подтверждён."
        confirmText="Отклонить"
        loading={rejecting}
      />
    </div>
  );
}
